import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import employeeService from '../../services/employeeService';
import permitService from '../../services/permitService';


const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [employeesData, permitsData] = await Promise.all([
        employeeService?.getAll(),
        permitService?.getAll()
      ]);
      setEmployees(employeesData);
      setPermits(permitsData);
    } catch (err) {
      setError(err?.message || 'Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees?.filter(emp => {
    const matchesSearch = 
      emp?.firstName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.lastName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.employeeNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      emp?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    if (selectedFilter === 'all') return matchesSearch;
    if (selectedFilter === 'citizen') return matchesSearch && emp?.citizenStatus === 'citizen';
    if (selectedFilter === 'non_citizen') return matchesSearch && emp?.citizenStatus === 'non_citizen';
    if (selectedFilter === 'active') return matchesSearch && emp?.isActive;
    return matchesSearch;
  }) || [];

  const stats = {
    total: employees?.length || 0,
    active: employees?.filter(e => e?.isActive)?.length || 0,
    citizens: employees?.filter(e => e?.citizenStatus === 'citizen')?.length || 0,
    nonCitizens: employees?.filter(e => e?.citizenStatus === 'non_citizen')?.length || 0,
    expiringPermits: permits?.filter(p => {
      const daysUntilExpiry = Math.floor((new Date(p?.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 90 && daysUntilExpiry >= 0;
    })?.length || 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader2" className="animate-spin h-8 w-8 mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading employee data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <main className="container mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Employee Management</h1>
          <p className="text-muted-foreground">Manage employee registry, contracts, and work permits</p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-6">
            <p className="font-medium">Error loading data</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Users" size={24} className="text-primary" />
              <span className="text-2xl font-bold text-foreground">{stats?.total}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Employees</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="CheckCircle" size={24} className="text-success" />
              <span className="text-2xl font-bold text-foreground">{stats?.active}</span>
            </div>
            <p className="text-sm text-muted-foreground">Active Employees</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Flag" size={24} className="text-accent" />
              <span className="text-2xl font-bold text-foreground">{stats?.citizens}</span>
            </div>
            <p className="text-sm text-muted-foreground">PNG Citizens</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Globe" size={24} className="text-primary" />
              <span className="text-2xl font-bold text-foreground">{stats?.nonCitizens}</span>
            </div>
            <p className="text-sm text-muted-foreground">Non-Citizens</p>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="AlertTriangle" size={24} className="text-warning" />
              <span className="text-2xl font-bold text-foreground">{stats?.expiringPermits}</span>
            </div>
            <p className="text-sm text-muted-foreground">Permits Expiring</p>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFilter('all')}
              >
                All Employees
              </Button>
              <Button
                variant={selectedFilter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFilter('active')}
              >
                Active Only
              </Button>
              <Button
                variant={selectedFilter === 'citizen' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFilter('citizen')}
              >
                Citizens
              </Button>
              <Button
                variant={selectedFilter === 'non_citizen' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedFilter('non_citizen')}
              >
                Non-Citizens
              </Button>
            </div>

            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee #</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Position</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Citizenship</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-muted-foreground">
                      No employees found
                    </td>
                  </tr>
                ) : (
                  filteredEmployees?.map((employee) => (
                    <tr key={employee?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-foreground">{employee?.employeeNumber}</td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-sm font-medium text-foreground">{employee?.firstName} {employee?.lastName}</p>
                          <p className="text-xs text-muted-foreground">{employee?.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">{employee?.department}</td>
                      <td className="py-3 px-4 text-sm text-foreground">{employee?.position}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {employee?.employmentType?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {employee?.isActive ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                            <Icon name="CheckCircle" size={12} className="mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                            <Icon name="XCircle" size={12} className="mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {employee?.citizenStatus === 'citizen' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent-foreground">
                            <Icon name="Flag" size={12} className="mr-1" />
                            Citizen
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            <Icon name="Globe" size={12} className="mr-1" />
                            Non-Citizen
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" iconName="Eye">
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {permits?.filter(p => {
          const daysUntilExpiry = Math.floor((new Date(p?.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
          return daysUntilExpiry <= 90 && daysUntilExpiry >= 0;
        })?.length > 0 && (
          <div className="bg-warning/10 border border-warning rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Work Permits Expiring Soon</h3>
                <p className="text-sm text-muted-foreground">The following work permits require attention within 90 days</p>
              </div>
            </div>
            <div className="space-y-3">
              {permits?.filter(p => {
                const daysUntilExpiry = Math.floor((new Date(p?.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                return daysUntilExpiry <= 90 && daysUntilExpiry >= 0;
              })?.map((permit) => {
                const daysUntilExpiry = Math.floor((new Date(permit?.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={permit?.id} className="bg-card rounded-md p-4 border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">
                          {permit?.employee?.firstName} {permit?.employee?.lastName}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Permit #{permit?.permitNumber} • {permit?.employee?.department}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-warning">
                          {daysUntilExpiry} days remaining
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Expires: {new Date(permit?.expiryDate)?.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmployeeManagement;